/**
 * A linked credit-note line against the original line it refunds (DGI
 * §2.29): which original line a credit line counts towards, what the
 * credit notes counted already credited of each line, and each new line's
 * share of its original line, refused at another price than that line's
 * (AVOIR_PRIX_LIGNE_ORIGINE), beyond what remains of it
 * (AVOIR_QUANTITE_DEPASSE_LIGNE), off the centime
 * (AVOIR_QUANTITE_HORS_CENTIME), or as a COR price credit the line cannot
 * take (AVOIR_CORRECTION_PRIX_INVALIDE). Exact arithmetic (`ExactDecimal`);
 * read by the API and the standalone MCF agent.
 */
import { CREDIT_NOTE_TYPES } from "../constants/credit-note-types.js";
import { INVOICE_TYPES, getCreditNoteTypeFor } from "../constants/invoice-types.js";
import { InvoiceError } from "../errors/index.js";
import { creditLineDiscount, creditedQuantityCandidates, isPriceCreditLine, priceCreditProposal, } from "./credit-note-line.js";
import { ExactDecimal } from "./exact-decimal.js";
/** The credit-note type of an original of `originalType`: FA or EA; refused for any document but a sale (FV, FT, EV, ET). */
export function creditNoteTypeOf(originalType) {
    const type = Object.hasOwn(INVOICE_TYPES, originalType)
        ? getCreditNoteTypeFor(originalType)
        : null;
    if (type !== "FA" && type !== "EA") {
        throw new InvoiceError(`Un avoir ne peut pas être émis sur un document de type ${originalType} : seules les factures de vente et d'acompte (FV, FT, EV, ET) peuvent être créditées`, "AVOIR_TYPE_ORIGINE", 400);
    }
    return type;
}
/**
 * The line of the original a credit line refunds when it names its article
 * and not the line: the only line of the article, or among several the
 * only one with the requested VAT group (and PSVB group, when sent); null
 * when the original does not carry the article. Any other request is
 * refused.
 */
export function originalLineByArticle(line, index, originalLines) {
    const candidates = originalLines.filter((candidate) => candidate.articleId === line.articleId);
    if (candidates.length <= 1)
        return candidates[0] ?? null;
    const psvbSent = line.psvbGroup !== undefined && line.psvbGroup !== null && line.psvbGroup !== "";
    const matching = candidates.filter((candidate) => candidate.taxGroup === line.taxGroup && (!psvbSent || candidate.psvbGroup === line.psvbGroup));
    if (matching.length === 1)
        return matching[0];
    throw new InvoiceError(`Ligne ${index + 1} : l'article « ${line.articleCode} » figure sur ${candidates.length} lignes de la facture ` +
        `d'origine, dont ${matching.length === 0 ? "aucune" : "plusieurs"} au groupe ${line.taxGroup} : ` +
        "désignez la ligne d'origine remboursée", "AVOIR_LIGNE_ORIGINE_AMBIGUE", 400);
}
export const NOTHING_CREDITED = {
    quantity: new ExactDecimal(0),
    discount: new ExactDecimal(0),
    priceCredit: new ExactDecimal(0),
    priceReduction: new ExactDecimal(0),
};
/**
 * `sum` with one more credit line of `originalLine`: a price credit
 * (`isPriceCreditLine`) lowers the price of the units still on the line and
 * returns none; any other line returns its quantity with its discount.
 */
export function withCredit(sum, line, originalLine) {
    if (originalLine && isPriceCreditLine(new ExactDecimal(line.unitPrice).toNumber(), new ExactDecimal(originalLine.unitPrice).toNumber())) {
        return {
            ...sum,
            priceCredit: sum.priceCredit.add(new ExactDecimal(line.quantity).mul(line.unitPrice)),
            priceReduction: sum.priceReduction.add(line.unitPrice),
        };
    }
    return { ...sum, quantity: sum.quantity.add(line.quantity), discount: sum.discount.add(line.discount) };
}
/**
 * The one original line with the article, VAT group, PSVB group and unit
 * price of a credit line stored without its original line; null when none
 * or several match.
 */
export function uniquelyMatchingLine(line, originalLines) {
    const matching = originalLines.filter((candidate) => candidate.articleId === line.articleId &&
        candidate.taxGroup === line.taxGroup &&
        (candidate.psvbGroup ?? null) === (line.psvbGroup ?? null) &&
        new ExactDecimal(candidate.unitPrice).equals(line.unitPrice));
    return matching.length === 1 ? matching[0] : null;
}
/** The id of the original line a credit line counts towards: the one it names, else the one it matches. */
export function creditedLineId(line, originalLines) {
    return line.originalLineId ?? uniquelyMatchingLine(line, originalLines)?.id ?? null;
}
/** Prior credit lines in the order their credit notes were signed, then drafted. */
function inSigningOrder(priorLines) {
    const at = (line) => [
        line.invoice?.signedAt?.getTime() ?? Number.MAX_SAFE_INTEGER,
        line.invoice?.createdAt.getTime() ?? 0,
        line.lineNumber ?? 0,
    ];
    return [...priorLines].sort((a, b) => {
        const [x, y] = [at(a), at(b)];
        return x[0] - y[0] || x[1] - y[1] || x[2] - y[2];
    });
}
/**
 * Whether `prior`, a credit line of `originalLine` priced otherwise than it,
 * is a proven price credit: on a COR credit note, covering every unit left
 * once `returned` units were returned before it.
 */
export function isProvenPriceCredit(prior, originalLine, returned) {
    return (prior.invoice?.creditNoteType === CREDIT_NOTE_TYPES.COR.code &&
        new ExactDecimal(prior.quantity).equals(new ExactDecimal(originalLine.quantity).sub(returned)));
}
/**
 * What prior credit lines credited of each original line, by its id, in
 * signing order (`inSigningOrder`), and the articles whose remainder is
 * unknown: one of their prior lines no original line takes
 * (`creditedLineId`), or a line priced otherwise than its original line that
 * is no proven price credit (`isProvenPriceCredit`), which still counts as a
 * return. `priceCredits` are the proven price credits.
 */
export function creditedByOriginalLine(priorLines, originalLines) {
    const credited = new Map();
    const unattributedArticles = new Set();
    const priceCredits = new Set();
    for (const prior of inSigningOrder(priorLines)) {
        const target = creditedLineId(prior, originalLines);
        const originalLine = originalLines.find((candidate) => candidate.id === target);
        if (target === null) {
            unattributedArticles.add(prior.articleId);
            continue;
        }
        const sum = credited.get(target) ?? NOTHING_CREDITED;
        const priced = originalLine !== undefined &&
            isPriceCreditLine(new ExactDecimal(prior.unitPrice).toNumber(), new ExactDecimal(originalLine.unitPrice).toNumber());
        if (priced && !isProvenPriceCredit(prior, originalLine, sum.quantity)) {
            unattributedArticles.add(prior.articleId);
            credited.set(target, withCredit(sum, prior, undefined));
            continue;
        }
        if (priced)
            priceCredits.add(prior);
        credited.set(target, withCredit(sum, prior, originalLine));
    }
    return { credited, unattributedArticles, priceCredits };
}
/**
 * Refuses a linked credit-note line whose unit price is not the one of the
 * original line it refunds: the line hands back that line as it was sold,
 * and a part of it is credited by its quantity.
 */
export function assertOriginalLinePrice(line, index, originalLine) {
    const price = new ExactDecimal(line.unitPrice);
    if (price.equals(originalLine.unitPrice))
        return;
    throw new InvoiceError(`Ligne ${index + 1} : le prix unitaire de l'avoir (${price.toString()}) n'est pas celui de la ligne d'origine ` +
        `(${new ExactDecimal(originalLine.unitPrice).toString()}) : une ligne d'avoir rattachée reprend le prix de sa ligne ` +
        "d'origine ; une partie de la ligne se crédite par sa quantité.", "AVOIR_PRIX_LIGNE_ORIGINE", 400);
}
/**
 * The unit price the units left on `originalLine` are sold at now: their
 * value, net of the discount and the price credits the returns have not
 * taken, per unit; the line's price less its price credits when none is left
 * or the remainder is unknown.
 */
export function effectiveUnitPrice(originalLine, remainder) {
    const price = new ExactDecimal(originalLine.unitPrice);
    if (remainder.remainingQuantity <= 0 || remainder.remainingDiscount === null) {
        return price.sub(remainder.priceReduction ?? 0);
    }
    return price.sub(new ExactDecimal(remainder.remainingDiscount).div(remainder.remainingQuantity));
}
/**
 * Refuses a COR price credit of `quantity` units at `unitPrice` that its
 * original line cannot take (AVOIR_CORRECTION_PRIX_INVALIDE): on a line with
 * a TSB or a specific tax, or whose remainder is unknown; not covering every
 * unit left; leaving a rest whose gross is not to the centime (the MCF takes
 * no discount on it); not below the effective unit price; or crediting
 * `quantity × unitPrice` with a fraction of a centime, with the nearest new
 * price that does not (`priceCreditProposal`).
 */
function assertPriceCredit(index, quantity, unitPrice, originalLine, remainder) {
    const refuse = (reason) => {
        throw new InvoiceError(`Ligne ${index + 1} : correction de prix refusée : ${reason}`, "AVOIR_CORRECTION_PRIX_INVALIDE", 400);
    };
    if (originalLine.isBeverage || originalLine.specificTaxMode !== null) {
        refuse("la ligne d'origine porte une TSB ou une taxe spécifique, et le MCF ne reçoit pas de remise sur une telle " +
            "ligne : son reste ne pourrait plus être crédité.");
    }
    if (remainder.remainingDiscount === null) {
        refuse("des avoirs antérieurs de cet article ne permettent pas d'établir ce qu'il reste de la ligne d'origine : son " +
            "prix effectif est inconnu.");
    }
    if (!quantity.equals(remainder.remainingQuantity)) {
        refuse(`elle doit porter sur toutes les unités restant à créditer de la ligne d'origine (${new ExactDecimal(remainder.remainingQuantity).toString()}), ` +
            `non sur ${quantity.toString()}.`);
    }
    const gross = quantity.mul(originalLine.unitPrice);
    if (!gross.mul(100).isInteger()) {
        refuse("aucune baisse de prix ne laisse le reste de la ligne certifiable : le montant brut des " +
            `${quantity.toString()} unités restantes (${gross.toString()} F) n'est pas au centime, et le MCF ne reçoit ` +
            "pas de remise sur un tel montant. Créditez ces unités par un avoir partiel, sans correction de prix.");
    }
    const effective = effectiveUnitPrice(originalLine, remainder);
    const netAfter = new ExactDecimal(originalLine.unitPrice)
        .mul(remainder.remainingQuantity)
        .sub(remainder.remainingDiscount)
        .sub(quantity.mul(unitPrice));
    if (unitPrice.greaterThanOrEqualTo(effective) || netAfter.lessThanOrEqualTo(0)) {
        refuse(`la baisse de prix (${unitPrice.toString()}) doit être inférieure au prix unitaire effectif de la ligne ` +
            `(${effective.toDecimalPlaces(2).toString()}) et lui laisser un montant positif.`);
    }
    const credit = quantity.mul(unitPrice);
    if (!credit.mul(100).isInteger()) {
        const from = effective.toDecimalPlaces(2);
        const proposal = priceCreditProposal(quantity.toNumber(), from.toNumber(), from.sub(unitPrice).toNumber(), () => true);
        refuse(`la baisse de prix (${unitPrice.toString()}) sur les ${quantity.toString()} unités restantes crédite ` +
            `${credit.toString()} F, un montant qui n'est pas au centime : le reste de la ligne ne pourrait plus être ` +
            "crédité jusqu'au bout. " +
            (proposal === null
                ? "Aucun nouveau prix unitaire proche ne la rend possible."
                : `Nouveau prix unitaire accepté le plus proche : ${new ExactDecimal(proposal.newPrice).toString()} ` +
                    `(baisse de ${new ExactDecimal(proposal.difference).toString()}).`));
    }
}
/**
 * What remains to credit of `originalLine` once `credited` is counted; its
 * discount is unknown when some prior credits of its article are not
 * attributed (`creditedByOriginalLine`).
 */
export function lineRemainder(originalLine, credited, unattributedArticles) {
    const sum = credited ?? NOTHING_CREDITED;
    const unknown = unattributedArticles.has(originalLine.articleId);
    return {
        originalQuantity: new ExactDecimal(originalLine.quantity).toNumber(),
        originalDiscount: new ExactDecimal(originalLine.discount).toNumber(),
        remainingQuantity: new ExactDecimal(originalLine.quantity).sub(sum.quantity).toNumber(),
        remainingDiscount: unknown ? null : new ExactDecimal(originalLine.discount).add(sum.priceCredit).sub(sum.discount).toNumber(),
        priceReduction: unknown ? 0 : sum.priceReduction.toNumber(),
    };
}
/** A decimal in a refusal, with a decimal comma and no grouping: « 3259,705 ». */
export function plainNumber(value) {
    return new ExactDecimal(value).toString().replace(".", ",");
}
/**
 * The French refusal of a linked part of `quantity` units whose amount,
 * quantity × price − discount, is not to the centime while it is not the
 * line's whole rest, with the nearest quantity whose amount is.
 */
function centimePartRefusal(index, quantity, unitPrice, discount, remainder) {
    const amountOf = (units) => new ExactDecimal(units).mul(unitPrice).sub(creditLineDiscount(units, remainder));
    const nearest = creditedQuantityCandidates(quantity.toNumber(), remainder.remainingQuantity).find((units) => units === remainder.remainingQuantity || amountOf(units).mul(100).isInteger());
    return (`Ligne ${index + 1} : le montant de ${plainNumber(quantity)} × ${plainNumber(unitPrice)}` +
        `${discount.isZero() ? "" : ` − ${plainNumber(discount)}`} (${plainNumber(quantity.mul(unitPrice).sub(discount))} F) ` +
        "ne tombe pas au centime : une partie de la ligne d'origine se crédite pour un montant au centime, ou en une " +
        `fois pour tout son reste (${plainNumber(new ExactDecimal(remainder.remainingQuantity))}).` +
        (nearest === undefined ? "" : ` Quantité à créditer au centime la plus proche : ${plainNumber(new ExactDecimal(nearest))}.`));
}
/**
 * Each line's share of the original line it refunds (`refundedLines`), null
 * without one, after prior credits and the earlier lines. A return, at that
 * line's price (AVOIR_PRIX_LIGNE_ORIGINE), at most what remains
 * (AVOIR_QUANTITE_DEPASSE_LIGNE), takes `creditLineDiscount`; a return
 * whose quantity × price − discount is not to the centime is the line's
 * whole rest or is refused (AVOIR_QUANTITE_HORS_CENTIME): every other part
 * is then exact, the rest rounds as the original line did, and the parts
 * add up to the line's entered amount. A COR line at another price is a
 * price credit (`assertPriceCredit`), without discount.
 */
export function linkedLineShares(lines, refundedLines, priorLines, originalLines, creditNoteType) {
    const { credited, unattributedArticles } = creditedByOriginalLine(priorLines, originalLines);
    return lines.map((line, index) => {
        const originalLine = refundedLines[index];
        if (!originalLine)
            return null;
        const sum = credited.get(originalLine.id) ?? NOTHING_CREDITED;
        const quantity = new ExactDecimal(line.quantity);
        const unitPrice = new ExactDecimal(line.unitPrice);
        const remainder = lineRemainder(originalLine, sum, unattributedArticles);
        if (isPriceCreditLine(unitPrice.toNumber(), new ExactDecimal(originalLine.unitPrice).toNumber())) {
            if (creditNoteType !== CREDIT_NOTE_TYPES.COR.code)
                assertOriginalLinePrice(line, index, originalLine);
            assertPriceCredit(index, quantity, unitPrice, originalLine, remainder);
            const discount = new ExactDecimal(0);
            credited.set(originalLine.id, withCredit(sum, { quantity, unitPrice, discount }, originalLine));
            return { discount, remainder, priceCredit: true };
        }
        assertOriginalLinePrice(line, index, originalLine);
        if (quantity.greaterThan(remainder.remainingQuantity)) {
            throw new InvoiceError(`Ligne ${index + 1} : la quantité de l'avoir (${quantity.toString()}) dépasse la quantité restant à créditer ` +
                `sur la ligne d'origine (${ExactDecimal.max(remainder.remainingQuantity, 0).toString()})`, "AVOIR_QUANTITE_DEPASSE_LIGNE", 400);
        }
        const discount = new ExactDecimal(creditLineDiscount(quantity.toNumber(), remainder));
        const toTheCentime = quantity.mul(unitPrice).sub(discount).mul(100).isInteger();
        if (!toTheCentime && !quantity.equals(remainder.remainingQuantity)) {
            throw new InvoiceError(centimePartRefusal(index, quantity, unitPrice, discount, remainder), "AVOIR_QUANTITE_HORS_CENTIME", 400);
        }
        credited.set(originalLine.id, withCredit(sum, { quantity, unitPrice, discount }, originalLine));
        return { discount, remainder, priceCredit: false };
    });
}
//# sourceMappingURL=credit-note-shares.js.map